import React, { useEffect, useState, useRef } from "react";
import classes from './AssetDocuments.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../Components/Footer/Footer";
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, Trash2, X, FileText, Upload, Download, File, Image } from 'lucide-react';
import searchicon from "../../Assets/searchicon.png";
import filter from "../../Assets/filter.png";
import axios from "axios";
import { BASE_URL } from "../../Components/API/Api";
import Swal from 'sweetalert2';

const AssetDocument = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isActive, setIsActive] = useState(false);
    const [selectedMetrics, setSelectedMetrics] = useState({
        total_documents: 0,
        total_assets: 0,
    });

    const [loading, setLoading] = useState(true);
    const [modalLoading, setModalLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [assets, setAssets] = useState([]);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [companyId, setCompanyId] = useState(null);

    // Modal state
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    // Upload state
    const [uploadFormData, setUploadFormData] = useState({
        asset_id: "",
        asset_document: []
    });
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    // For searchable selects
    const [searchTerms, setSearchTerms] = useState({
        asset: ""
    });
    const [showDropdowns, setShowDropdowns] = useState({
        asset: false
    });
    const assetRef = useRef(null);

    // Get company ID from localStorage
    useEffect(() => {
        const getCompanyId = () => {
            console.log("Attempting to get company ID from various sources...");
            
            const directCompanyId = localStorage.getItem("company_id");
            if (directCompanyId) {
                console.log("Found company_id in localStorage:", directCompanyId);
                setCompanyId(directCompanyId);
                setUploadFormData(prev => ({
                    ...prev,
                    company_id: directCompanyId
                }));
                return;
            }
            
            const companyInfo = localStorage.getItem("companyInfo");
            if (companyInfo) {
                try {
                    const parsed = JSON.parse(companyInfo);
                    const id = parsed?.id || parsed?.company_id || parsed?.companyId;
                    if (id) {
                        setCompanyId(String(id));
                        setUploadFormData(prev => ({
                            ...prev,
                            company_id: String(id)
                        }));
                        console.log("Using company ID from companyInfo:", id);
                        return;
                    }
                } catch (error) {
                    console.error("Error parsing companyInfo:", error);
                }
            }
            
            const userData = localStorage.getItem("user");
            if (userData) {
                try {
                    const parsed = JSON.parse(userData);
                    
                    if (parsed.company_id) {
                        setCompanyId(parsed.company_id);
                        setUploadFormData(prev => ({
                            ...prev,
                            company_id: parsed.company_id
                        }));
                        console.log("Using direct company_id from user:", parsed.company_id);
                        return;
                    }
                    
                    if (parsed.company) {
                        const companyId = parsed.company?.id || parsed.company?.company_id || parsed.company?.companyId;
                        if (companyId) {
                            setCompanyId(String(companyId));
                            setUploadFormData(prev => ({
                                ...prev,
                                company_id: String(companyId)
                            }));
                            console.log("Using company ID from user.company:", companyId);
                            return;
                        }
                    }
                    
                    if (parsed.companies && Array.isArray(parsed.companies) && parsed.companies.length > 0) {
                        const mainCompany = parsed.companies.find(c => c.is_main === "1" || c.is_main === 1);
                        if (mainCompany) {
                            const companyId = mainCompany?.id || mainCompany?.company_id || mainCompany?.companyId;
                            if (companyId) {
                                setCompanyId(String(companyId));
                                setUploadFormData(prev => ({
                                    ...prev,
                                    company_id: String(companyId)
                                }));
                                console.log("Using main company ID from companies array:", companyId);
                                return;
                            }
                        }
                        
                        const firstCompany = parsed.companies[0];
                        if (firstCompany) {
                            const companyId = firstCompany?.id || firstCompany?.company_id || firstCompany?.companyId;
                            if (companyId) {
                                setCompanyId(String(companyId));
                                setUploadFormData(prev => ({
                                    ...prev,
                                    company_id: String(companyId)
                                }));
                                console.log("Using first company ID from companies array:", companyId);
                                return;
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error parsing user data:", error);
                }
            }
            
            console.warn("No company ID found in any source");
        };
        
        getCompanyId();
    }, []);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (assetRef.current && !assetRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, asset: false }));
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch assets when component mounts
    useEffect(() => {
        if (companyId) {
            fetchAssets();
        }
    }, [companyId]);

    // Fetch documents when selected asset changes
    useEffect(() => {
        if (selectedAsset && companyId) {
            fetchAssetDocuments(selectedAsset.id);
        } else {
            setDocuments([]);
            setSelectedMetrics(prev => ({
                ...prev,
                total_documents: 0
            }));
        }
    }, [selectedAsset, companyId]);

    const fetchAssets = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            
            if (!token) {
                console.error("No auth token found");
                Swal.fire({
                    icon: 'error',
                    title: 'Authentication Error',
                    text: 'Please log in again'
                });
                setLoading(false);
                return;
            }

            if (!companyId) {
                console.error("Company ID is required");
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Company ID not found. Please log in again.'
                });
                setLoading(false);
                return;
            }

            console.log(`Fetching assets for company ID: ${companyId}`);
            
            const response = await axios.get(`${BASE_URL}/v1/fixedassets/fetch`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    company_id: companyId
                }
            });

            console.log("Assets response:", response.data);

            if (response.data.status && Array.isArray(response.data.data)) {
                setAssets(response.data.data);
                
                setSelectedMetrics(prev => ({
                    ...prev,
                    total_assets: response.data.data.length
                }));
                
                console.log(`Loaded ${response.data.data.length} assets`);
            } else {
                console.warn("Unexpected response format:", response.data);
                setAssets([]);
            }
        } catch (error) {
            console.error("Error fetching assets:", error);
            
            if (error.response) {
                console.error("Error response data:", error.response.data);
                console.error("Error response status:", error.response.status);
                
                let errorMessage = "Failed to fetch assets";
                if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.status === 401) {
                    errorMessage = "Unauthorized. Please log in again.";
                } else if (error.response.status === 404) {
                    errorMessage = "API endpoint not found";
                }
                
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: errorMessage
                });
            } else if (error.request) {
                console.error("No response received:", error.request);
                Swal.fire({
                    icon: 'error',
                    title: 'Network Error',
                    text: 'No response from server. Please check your connection.'
                });
            } else {
                console.error("Error setting up request:", error.message);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'An error occurred while setting up the request'
                });
            }
            
            setAssets([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssetDocuments = async (assetId) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            
            if (!token) {
                console.error("No auth token found");
                Swal.fire({
                    icon: 'error',
                    title: 'Authentication Error',
                    text: 'Please log in again'
                });
                setLoading(false);
                return;
            }

            if (!companyId) {
                console.error("Company ID is required");
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Company ID not found. Please log in again.'
                });
                setLoading(false);
                return;
            }

            console.log(`Fetching documents for asset ID: ${assetId}`);
            
            const response = await axios.get(`${BASE_URL}/v1/fixedassets/fetch-attached-documents`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    asset_id: assetId,
                    company_id: companyId
                }
            });

            console.log("Documents response:", response.data);

            if (response.data.status && Array.isArray(response.data.data)) {
                setDocuments(response.data.data);
                
                setSelectedMetrics(prev => ({
                    ...prev,
                    total_documents: response.data.data.length
                }));
                
                console.log(`Loaded ${response.data.data.length} documents`);
            } else {
                console.warn("Unexpected response format:", response.data);
                setDocuments([]);
            }
        } catch (error) {
            console.error("Error fetching documents:", error);
            
            if (error.response) {
                console.error("Error response data:", error.response.data);
                console.error("Error response status:", error.response.status);
                
                let errorMessage = "Failed to fetch documents";
                if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }
                
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: errorMessage
                });
            } else if (error.request) {
                console.error("No response received:", error.request);
                Swal.fire({
                    icon: 'error',
                    title: 'Network Error',
                    text: 'No response from server. Please check your connection.'
                });
            } else {
                console.error("Error setting up request:", error.message);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'An error occurred while setting up the request'
                });
            }
            
            setDocuments([]);
        } finally {
            setLoading(false);
        }
    };

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
        '/fixed-assets': 'Fixed Assets',
    };

    // Handle file selection
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        
        // Validate file types and sizes
        const validFiles = files.filter(file => {
            const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
            const isValidType = validTypes.includes(file.type);
            const isValidSize = file.size <= 2 * 1024 * 1024; // 2MB
            
            if (!isValidType) {
                Swal.fire('Error', `${file.name} is not a valid file type. Only PDF, JPG, JPEG, and PNG are allowed.`, 'error');
            }
            if (!isValidSize) {
                Swal.fire('Error', `${file.name} exceeds the 2MB size limit.`, 'error');
            }
            
            return isValidType && isValidSize;
        });
        
        setUploadedFiles(prev => [...prev, ...validFiles]);
        setUploadFormData(prev => ({
            ...prev,
            asset_document: [...(prev.asset_document || []), ...validFiles]
        }));
    };

    const removeFile = (index) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
        setUploadFormData(prev => ({
            ...prev,
            asset_document: prev.asset_document.filter((_, i) => i !== index)
        }));
    };

    // Handle searchable select for asset
    const handleSearchChange = (field, e) => {
        setSearchTerms(prev => ({ ...prev, [field]: e.target.value }));
        if (!showDropdowns[field]) {
            setShowDropdowns(prev => ({ ...prev, [field]: true }));
        }
    };

    const handleSelect = (field, item) => {
        setSelectedAsset(item);
        setUploadFormData(prev => ({
            ...prev,
            asset_id: item.id
        }));
        
        const displayName = `${item.asset_name} (${item.asset_code})`;
        setSearchTerms(prev => ({ ...prev, asset: displayName }));
        setShowDropdowns(prev => ({ ...prev, asset: false }));
    };

    const getSelectedName = (field) => {
        if (!selectedAsset) return "";
        return `${selectedAsset.asset_name} (${selectedAsset.asset_code})`;
    };

    const filterItems = (field) => {
        const searchTerm = (searchTerms.asset || "").toLowerCase();
        
        if (!Array.isArray(assets)) return [];
        
        return assets.filter(item => {
            if (!item) return false;
            
            const assetName = (item.asset_name || "").toLowerCase();
            const assetCode = (item.asset_code || "").toLowerCase();
            return assetName.includes(searchTerm) || assetCode.includes(searchTerm);
        });
    };

    // Handle upload submit
    const handleUploadSubmit = async (e) => {
        e.preventDefault();

        if (!uploadFormData.asset_id) {
            Swal.fire("Error", "Please select an asset", "error");
            return;
        }

        if (uploadedFiles.length === 0) {
            Swal.fire("Error", "Please select at least one file to upload", "error");
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem("authToken");

            const formData = new FormData();
            formData.append('asset_id', uploadFormData.asset_id);
            formData.append('company_id', companyId);
            
            uploadedFiles.forEach(file => {
                formData.append('asset_document[]', file);
            });

            console.log("Uploading documents for asset:", uploadFormData.asset_id);

            const response = await axios.post(
                `${BASE_URL}/v1/fixedassets/attach-document`,
                formData,
                { 
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    } 
                }
            );

            console.log("Upload response:", response.data);

            if (response.data?.status) {
                Swal.fire({
                    title: "Success",
                    text: response.data.message || "Documents uploaded successfully",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false
                });
                
                resetUploadForm();
                onClose();
                if (selectedAsset) {
                    fetchAssetDocuments(selectedAsset.id);
                }
            } else {
                Swal.fire("Error", response.data?.message || "Failed to upload documents", "error");
            }
        } catch (error) {
            console.error("Error uploading documents:", error);
            
            let errorMessage = "Something went wrong while uploading documents";
            if (error.response?.data?.message) {
                if (typeof error.response.data.message === 'object') {
                    errorMessage = Object.values(error.response.data.message).flat().join(', ');
                } else {
                    errorMessage = error.response.data.message;
                }
            }
            
            Swal.fire("Error", errorMessage, "error");
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete document
    const handleDeleteDocument = async (document) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            setDeleteLoading(true);
            try {
                const token = localStorage.getItem("authToken");
                
                // Note: You'll need to implement the delete document endpoint
                const response = await axios.delete(
                    `${BASE_URL}/v1/fixedassets/delete-document/${document.id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        params: {
                            company_id: companyId
                        }
                    }
                );

                console.log("Delete response:", response.data);

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: response.data.message || 'Document deleted successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                    
                    // Refresh documents
                    if (selectedAsset) {
                        fetchAssetDocuments(selectedAsset.id);
                    }
                } else {
                    throw new Error(response.data.message || "Failed to delete document");
                }
            } catch (error) {
                console.error("Error deleting document:", error);

                let errorMessage = "Failed to delete document";
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            } finally {
                setDeleteLoading(false);
            }
        }
    };

    // Handle view document
    const handleViewDocument = (document) => {
        setSelectedDocument(document);
        setShowViewModal(true);
    };

    // Handle download document
    const handleDownloadDocument = (document) => {
        if (document.file_url) {
            window.open(document.file_url, '_blank');
        } else {
            Swal.fire('Error', 'Document file not found', 'error');
        }
    };

    // Get file icon based on file type
    const getFileIcon = (fileName) => {
        const extension = fileName?.split('.').pop()?.toLowerCase();
        if (extension === 'pdf') {
            return <File size={20} color="#dc2626" />;
        } else if (['jpg', 'jpeg', 'png'].includes(extension)) {
            return <Image size={20} color="#3b82f6" />;
        } else {
            return <File size={20} color="#6b7280" />;
        }
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (!bytes) return "0 B";
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    const resetUploadForm = () => {
        setUploadFormData({
            asset_id: "",
            asset_document: []
        });
        setUploadedFiles([]);
        setSearchTerms({
            asset: ""
        });
    };

    const onClose = () => {
        setShowUploadModal(false);
        resetUploadForm();
        setShowDropdowns({
            asset: false
        });
    };

    // Skeleton loader
    const TableSkeleton = () => {
        return Array.from({ length: 3 }).map((_, index) => (
            <tr key={index} className={classes.skeletonRow}>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                    </div>
                </td>
            </tr>
        ));
    };

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname] || "Fixed Assets"} />

            <div className={classes.Container}>
                <div className={classes.Container}>
                    <div className={classes.actions}>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/assets')}>
                            Assets
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/categories')}>
                            Categories
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/locations')}>
                            Locations
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/assignments')}>
                            Assignments
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/depreciation')}>
                            Depreciation
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/revaluation')}>
                            Revaluation
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/disposal')}>
                            Disposal
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/maintenance')}>
                            Maintenance
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/movements')}>
                            Movements
                        </button>
                        <button className={`${classes.btns} ${isActive ? classes.active : ""}`} onClick={() => setIsActive(true)}>
                            Documents
                        </button>
                    </div>

                    <header className={classes.Header}>
                        <h2 className={classes.Title}>Asset Documents</h2>
                    </header>

                    <div className={classes.entrjj}>
                        <div className={classes.allkads}>
                            <div className={classes.thrdkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Documents</p>
                                    <p className={classes.kdptg1}>
                                        {selectedMetrics?.total_documents?.toLocaleString() ?? '0'}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Uploaded files</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.frtkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Assets</p>
                                    <p className={classes.kdptg2}>
                                        {selectedMetrics?.total_assets?.toLocaleString() ?? '0'}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Available assets</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={classes.headerActions}>
                        <div className={classes.actionsRight}>
                            <div className={classes.inputWrapper}>
                                <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
                                <input type="text" className={classes.searchBar} placeholder="Search documents" />
                            </div>
                            <div className={classes.selectWrapper}>
                                <select 
                                    className={classes.dropdown}
                                    value={selectedAsset?.id || ""}
                                    onChange={(e) => {
                                        const asset = assets.find(a => a.id === e.target.value);
                                        setSelectedAsset(asset || null);
                                        if (asset) {
                                            fetchAssetDocuments(asset.id);
                                        } else {
                                            setDocuments([]);
                                            setSelectedMetrics(prev => ({
                                                ...prev,
                                                total_documents: 0
                                            }));
                                        }
                                    }}
                                >
                                    <option value="">Select Asset</option>
                                    {assets.map(asset => (
                                        <option key={asset.id} value={asset.id}>
                                            {asset.asset_name} ({asset.asset_code})
                                        </option>
                                    ))}
                                </select>
                                <span className={classes.dropdownArrow}>▼</span>
                            </div>
                            <div className={classes.inputWrapper}>
                                <input type="text" className={classes.searchBars} placeholder="Filter..." />
                                <img src={filter} alt="Filter" className={classes.inputIcon} />
                            </div>
                        </div>

                        <button className={classes.btnss} onClick={() => setShowUploadModal(true)}>
                            <Upload size={16} style={{ marginRight: "5px" }} />
                            Upload Documents
                        </button>
                    </div>

                    {selectedAsset ? (
                        <table className={classes.invoiceTable}>
                            <thead>
                                <tr>
                                    <th>S/N</th>
                                    <th>File Name</th>
                                    <th>File Type</th>
                                    <th>Size</th>
                                    <th>Uploaded Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <TableSkeleton />
                                ) : documents.length > 0 ? (
                                    documents.map((doc, index) => {
                                        const fileName = doc.file_name || doc.original_name || "Document";
                                        const fileExt = fileName.split('.').pop()?.toUpperCase() || "—";
                                        
                                        return (
                                            <tr key={doc.id || index}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <div className={classes.fileNameCell}>
                                                        {getFileIcon(fileName)}
                                                        <span>{fileName}</span>
                                                    </div>
                                                </td>
                                                <td>{fileExt}</td>
                                                <td>{formatFileSize(doc.file_size || 0)}</td>
                                                <td>{doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "—"}</td>
                                                <td>
                                                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                                        <Eye
                                                            size={16}
                                                            style={{ cursor: "pointer", color: "#007bff" }}
                                                            onClick={() => handleViewDocument(doc)}
                                                        />
                                                        <Download
                                                            size={16}
                                                            style={{ cursor: "pointer", color: "#16A34A" }}
                                                            onClick={() => handleDownloadDocument(doc)}
                                                        />
                                                        <Trash2
                                                            size={16}
                                                            style={{ cursor: "pointer", color: "#dc3545" }}
                                                            onClick={() => handleDeleteDocument(doc)}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
                                            No documents found for this asset
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    ) : (
                        <div className={classes.noAssetSelected}>
                            <FileText size={48} color="#9ca3af" />
                            <p>Please select an asset to view its documents</p>
                        </div>
                    )}
                </div>
            </div>

            {/* View Document Modal */}
            {showViewModal && selectedDocument && (
                <div className={classes.overlay}>
                    <div className={classes.modal} style={{ maxWidth: "600px" }}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <FileText className={classes.icon} />
                                <h2>Document Details</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={() => setShowViewModal(false)}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className={classes.documentDetails}>
                            <div className={classes.documentPreview}>
                                {selectedDocument.file_url ? (
                                    (selectedDocument.file_name || selectedDocument.original_name || "").toLowerCase().includes('.pdf') ? (
                                        <File size={64} color="#dc2626" />
                                    ) : (
                                        <img 
                                            src={selectedDocument.file_url} 
                                            alt={selectedDocument.file_name || selectedDocument.original_name}
                                            className={classes.previewImage}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.style.display = 'none';
                                                e.target.parentNode.innerHTML = '<svg class="fallback-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>';
                                            }}
                                        />
                                    )
                                ) : (
                                    <File size={64} color="#9ca3af" />
                                )}
                            </div>

                            <div className={classes.documentInfo}>
                                <p><strong>File Name:</strong> {selectedDocument.file_name || selectedDocument.original_name}</p>
                                <p><strong>File Type:</strong> {(selectedDocument.file_name || selectedDocument.original_name || "").split('.').pop()?.toUpperCase()}</p>
                                <p><strong>Size:</strong> {formatFileSize(selectedDocument.file_size || 0)}</p>
                                <p><strong>Uploaded:</strong> {selectedDocument.created_at ? new Date(selectedDocument.created_at).toLocaleString() : "—"}</p>
                            </div>

                            <div className={classes.documentActions}>
                                <button 
                                    className={classes.downloadBtn}
                                    onClick={() => handleDownloadDocument(selectedDocument)}
                                >
                                    <Download size={16} />
                                    Download
                                </button>
                                <button 
                                    className={classes.closeBtn2}
                                    onClick={() => setShowViewModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Modal */}
            {showUploadModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal} style={{ maxWidth: "600px" }}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <Upload className={classes.icon} />
                                <h2>Upload Asset Documents</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={onClose}>
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleUploadSubmit}>
                            <div className={classes.formGrid}>
                                <div className={classes.formGroupFull}>
                                    <label>Select Asset *</label>
                                    <div className={classes.searchSelectWrapper} ref={assetRef}>
                                        <div className={classes.searchInputContainer}>
                                            <input
                                                type="text"
                                                className={classes.searchSelectInput}
                                                placeholder="Search and select asset..."
                                                value={showDropdowns.asset ? searchTerms.asset : getSelectedName('asset')}
                                                onChange={(e) => handleSearchChange('asset', e)}
                                                onClick={() => setShowDropdowns(prev => ({ ...prev, asset: true }))}
                                                onFocus={() => setShowDropdowns(prev => ({ ...prev, asset: true }))}
                                                required
                                            />
                                        </div>

                                        {showDropdowns.asset && (
                                            <div className={classes.searchDropdown}>
                                                <div className={classes.dropdownList}>
                                                    {loading ? (
                                                        <div className={classes.dropdownItem}>Loading assets...</div>
                                                    ) : filterItems('asset').length === 0 ? (
                                                        <div className={classes.dropdownItem}>No assets found</div>
                                                    ) : (
                                                        filterItems('asset').map((item) => (
                                                            <div
                                                                key={item.id}
                                                                className={`${classes.dropdownItem} ${selectedAsset?.id === item.id ? classes.selectedItem : ''}`}
                                                                onClick={() => handleSelect('asset', item)}
                                                            >
                                                                {item.asset_name} ({item.asset_code})
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={classes.formGroupFull}>
                                    <label>Upload Documents *</label>
                                    <div className={classes.fileUploadArea}>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                            multiple
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            style={{ display: 'none' }}
                                        />
                                        <button
                                            type="button"
                                            className={classes.uploadBtn}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <Upload size={16} />
                                            Select Files
                                        </button>
                                        <span className={classes.fileHint}>
                                            (PDF, JPG, JPEG, PNG - Max 2MB each)
                                        </span>
                                    </div>
                                    
                                    {uploadedFiles.length > 0 && (
                                        <div className={classes.fileList}>
                                            {uploadedFiles.map((file, index) => {
                                                const fileExt = file.name.split('.').pop()?.toLowerCase();
                                                const isPdf = fileExt === 'pdf';
                                                const isImage = ['jpg', 'jpeg', 'png'].includes(fileExt || '');
                                                
                                                return (
                                                    <div key={index} className={classes.fileItem}>
                                                        {isPdf ? (
                                                            <File size={16} color="#dc2626" />
                                                        ) : isImage ? (
                                                            <Image size={16} color="#3b82f6" />
                                                        ) : (
                                                            <File size={16} color="#6b7280" />
                                                        )}
                                                        <span className={classes.fileName}>{file.name}</span>
                                                        <span className={classes.fileSize}>
                                                            ({(file.size / 1024).toFixed(2)} KB)
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className={classes.removeFileBtn}
                                                            onClick={() => removeFile(index)}
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button 
                                type="submit" 
                                className={classes.saveBtn} 
                                disabled={submitting || uploadedFiles.length === 0}
                            >
                                {submitting ? "Uploading..." : "Upload Documents"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default AssetDocument;